import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import InlineFeedback from '../components/InlineFeedback';

const UserProfile = ({ setNotification }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingZipOnly, setSavingZipOnly] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [profileRole, setProfileRole] = useState('');
  const [form, setForm] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    dob: '',
    zip_code: '',
    bio: '',
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('userprofiles/me/');

        const profile = response.data;
        setProfileRole(profile.role || '');
        setForm({
          username: profile.user?.username || '',
          email: profile.user?.email || '',
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          phone_number: profile.phone_number || '',
          dob: profile.dob || '',
          zip_code: profile.zip_code || '',
          bio: profile.bio || '',
        });
      } catch (error) {
        setFeedback({ type: 'error', message: 'Failed to load profile.' });
        setNotification?.({ message: 'Failed to load profile.', type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [token, setNotification]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        user: {
          username: form.username,
          email: form.email,
        },
        first_name: form.first_name,
        last_name: form.last_name,
        phone_number: form.phone_number,
        dob: form.dob || null,
        zip_code: form.zip_code,
        bio: form.bio,
      };

      await api.patch('userprofiles/me/', payload);

      setFeedback({ type: 'success', message: 'Profile updated successfully.' });
      setNotification?.({ message: 'Profile updated successfully.', type: 'success' });
    } catch (error) {
      const errorMessage =
        error.response?.data?.user?.email?.[0] ||
        error.response?.data?.user?.username?.[0] ||
        error.response?.data?.detail ||
        'Failed to update profile.';

      setFeedback({ type: 'error', message: errorMessage });
      setNotification?.({ message: errorMessage, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveZipOnly = async () => {
    setSavingZipOnly(true);

    try {
      await api.patch('userprofiles/me/', { zip_code: form.zip_code.trim() });
      setForm((prev) => ({ ...prev, zip_code: prev.zip_code.trim() }));
      setFeedback({ type: 'success', message: 'Zip code updated successfully.' });
      setNotification?.({ message: 'Zip code updated successfully.', type: 'success' });
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        'Failed to update zip code.';

      setFeedback({ type: 'error', message: errorMessage });
      setNotification?.({ message: errorMessage, type: 'error' });
    } finally {
      setSavingZipOnly(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F6] flex items-center justify-center text-[#6D3B7E]">
        Loading profile...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F6] px-3 py-4 sm:px-4 sm:py-10">
      <div className="mx-auto max-w-2xl space-y-6 rounded-xl bg-white p-4 shadow-md sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-[#6D3B7E]">My Profile</h1>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="min-h-11 w-full rounded bg-[#6D3B7E] px-4 py-2 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E] focus-visible:ring-offset-2 sm:w-auto"
          >
            Home
          </button>
        </div>

        <InlineFeedback type={feedback.type || 'info'} message={feedback.message} />

        {profileRole === 'client' ? (
          <div className="rounded-lg border border-[#E5D4ED] bg-[#F9F3FC] p-3 text-sm text-[#4B2A56]">
            <p className="mb-2 font-semibold">Career paths</p>
            <div className="flex flex-wrap gap-4">
              <button
                type="button"
                onClick={() => navigate('/owner-application')}
                className="font-semibold underline"
              >
                Apply for owner access
              </button>
              <button
                type="button"
                onClick={() => navigate('/worker-application')}
                className="font-semibold underline"
              >
                Apply to join a company as worker
              </button>
            </div>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4" aria-busy={saving || savingZipOnly}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="profile-username" className="block text-sm mb-1">Username</label>
              <input
                id="profile-username"
                type="text"
                name="username"
                value={form.username}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E]"
                required
              />
            </div>
            <div>
              <label htmlFor="profile-email" className="block text-sm mb-1">Email</label>
              <input
                id="profile-email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E]"
                required
              />
            </div>
            <div>
              <label htmlFor="profile-first-name" className="block text-sm mb-1">First Name</label>
              <input
                id="profile-first-name"
                type="text"
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E]"
              />
            </div>
            <div>
              <label htmlFor="profile-last-name" className="block text-sm mb-1">Last Name</label>
              <input
                id="profile-last-name"
                type="text"
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E]"
              />
            </div>
            <div>
              <label htmlFor="profile-phone" className="block text-sm mb-1">Phone Number</label>
              <input
                id="profile-phone"
                type="text"
                name="phone_number"
                value={form.phone_number}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E]"
              />
            </div>
            <div>
              <label htmlFor="profile-dob" className="block text-sm mb-1">Date of Birth</label>
              <input
                id="profile-dob"
                type="date"
                name="dob"
                value={form.dob}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E]"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="profile-zip" className="block text-sm mb-1">Zip Code</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  id="profile-zip"
                  type="text"
                  name="zip_code"
                  value={form.zip_code}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E]"
                  maxLength={10}
                />
                <button
                  type="button"
                  onClick={handleSaveZipOnly}
                  disabled={savingZipOnly}
                  className="bg-[#6D3B7E] text-white px-4 py-2 rounded disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E]"
                >
                  {savingZipOnly ? 'Saving Zip...' : 'Save Zip'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Updating your zip here helps booking show nearby workers first.
              </p>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="profile-bio" className="block text-sm mb-1">Bio</label>
              <textarea
                id="profile-bio"
                name="bio"
                value={form.bio}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 min-h-[100px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E]"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-[#CDA349] hover:bg-[#b8913e] text-white py-2 rounded font-semibold disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E]"
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UserProfile;
